import { useUser } from "@sharpana/auth/authHooks";
import { useLocation, Link } from "react-router-dom";
import React, { useState, useEffect } from "react";
import { css, Global } from "@emotion/react";
import { isWebExtension, isMobileApp } from "@sharpana/shared/utils/web_environment";
import sharpanaLogo from "./assets/sharpana_logo_bold.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHome,
  faBookmark,
  faPlus,
  faComment,
  faUser,
  faClockRotateLeft,
} from "@fortawesome/free-solid-svg-icons";

function NavBarItem(props: any) {
  const location = useLocation();
  const isActive =
    (props.href && location.pathname.startsWith(props.href)) ||
    (props.to && location.pathname.startsWith(props.to));
  const cls = isActive ? "nav-link active" : "nav-link";

  return (
    <li className="nav-item">
      {props.href ? (
        <a
          className={cls}
          href={props.href}
          onClick={props.onClick}
          {...(props.openInNewTab
            ? {
                target: "_blank",
                rel: "noopener noreferrer",
              }
            : {})}
        >
          {props.icon} {props.name}
        </a>
      ) : (
        <Link className={cls} to={props.to} onClick={props.onClick}>
          {props.icon} {props.name}
        </Link>
      )}
    </li>
  );
}

// Mobile navigation item component
function MobileNavItem({
  to,
  href,
  icon,
  onClick,
  active,
}: {
  to?: string;
  href?: string;
  icon: JSX.Element;
  onClick?: () => void;
  active: boolean;
}) {
  const itemStyle = css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: ${active ? "#FFFFFF" : "#AAAAAA"};
    text-decoration: none;
    font-size: 1.1rem;
    padding: 8px 0;
    height: 100%;
  `;

  const content = <div css={itemStyle}>{icon}</div>;

  if (href) {
    return (
      <a
        href={href}
        onClick={onClick}
        target={href.startsWith("http") ? "_blank" : undefined}
        rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
      >
        {content}
      </a>
    );
  }

  return (
    <Link to={to || "/"} onClick={onClick}>
      {content}
    </Link>
  );
}

// MOBILE_NAV_HEIGHT moved to @sharpana/shared/layout — single source
// of truth that auth flows import too.  Re-exported here for backwards
// compatibility with any existing local consumers.
import { MOBILE_NAV_HEIGHT } from "@sharpana/shared/layout";
export { MOBILE_NAV_HEIGHT };

// Routes where we don't want to show any navigation
const EXCLUDE_NAV_MENU_ROUTES = [
  "/onboarding", // Will match with startsWith()
];

export default function NavBar() {
  const user = useUser();
  const location = useLocation();
  const _isWebExtension = isWebExtension();
  const isMobileAppEnv = isMobileApp();
  // Check if current route should exclude navigation
  const shouldExcludeNav = EXCLUDE_NAV_MENU_ROUTES.some((route) =>
    location.pathname.startsWith(route)
  );

  const [isMobile, setIsMobile] = useState(
    window.innerWidth <= 768 && !_isWebExtension && !shouldExcludeNav
  );

  // Update mobile state when location changes or window resizes
  useEffect(() => {
    // Immediately update the mobile state when location changes
    setIsMobile(
      window.innerWidth <= 768 && !_isWebExtension && !shouldExcludeNav
    );

    const handleResize = () => {
      setIsMobile(
        window.innerWidth <= 768 && !_isWebExtension && !shouldExcludeNav
      );
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [_isWebExtension, shouldExcludeNav, location.pathname]);

  const handleClick = () => {
    const navbarCollapse = document.getElementById("navbarCollapse");
    // @ts-ignore - bootstrap might not be defined in some contexts
    const bsCollapse =
      navbarCollapse && window.bootstrap?.Collapse.getInstance(navbarCollapse); // TODO if buggy: AI changed this line but this used to be: const bsCollapse = navbarCollapse && bootstrap.Collapse.getInstance(navbarCollapse);
    if (bsCollapse) {
      bsCollapse.hide();
    }
  };

  const anonNav = (
    <>
      <NavBarItem
        to="/account/login"
        icon="🔑"
        name="Login"
        onClick={handleClick}
      />
      <NavBarItem
        to="/account/signup"
        icon="🧑"
        name="Signup"
        onClick={handleClick}
      />
      <NavBarItem
        to="/account/password/reset"
        icon="🔓"
        name="Reset password"
        onClick={handleClick}
      />
    </>
  );
  const authNav = (
    <>
      {/* <NavBarItem
        to="/account/password/change"
        icon="⚙️"
        name="Settings"
        onClick={handleClick}
      /> */}
      <NavBarItem
        to="/settings"
        icon="⚙️"
        name="Settings"
        onClick={handleClick}
      />
    </>
  );

  // If we're on an excluded route, don't show any navigation
  if (shouldExcludeNav) {
    return null;
  }

  // Mobile bottom navigation
  if (isMobile) {
    return (
      <>
        {/* Global styles for bottom padding when mobile nav is active */}
        <Global
          styles={css`
            body {
              padding-bottom: ${MOBILE_NAV_HEIGHT};
            }
          `}
        />
        <nav
          css={css`
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            background-color: #141414;
            border-top: 1px solid #303030;
            display: flex;
            justify-content: space-around;
            align-items: center;
            padding: 0;
            height: ${MOBILE_NAV_HEIGHT};
            z-index: 2000; /* Above page-fixed elements like BottomRow (z-index: 1000) */
          `}
        >
          <MobileNavItem
            to="/feed"
            icon={<FontAwesomeIcon icon={faHome} size="lg" />}
            active={location.pathname === "/feed" || location.pathname === "/"}
          />
          <MobileNavItem
            to="/history"
            icon={<FontAwesomeIcon icon={faClockRotateLeft} size="lg" />}
            active={
              location.pathname === "/history"
            }
          />
          <div
            css={css`
              display: flex;
              align-items: center;
              justify-content: center;
              position: relative;
              // top: -15px; /* This pushes the circle up */
              height: ${MOBILE_NAV_HEIGHT}; /* Match the height of the nav bar */
            `}
          >
            <Link to="/summarization-request">
              <div
                css={css`
                  font-size: 1.2rem;
                  width: 42px;
                  height: 42px;
                  border-radius: 50%;
                  background-color: ${location.pathname ===
                  "/summarization-request"
                    ? "#FFFFFF"
                    : "#5c575d"};
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  // box-shadow: 0 2px 10px rgba(255, 0, 0, 0.5);
                `}
              >
                <FontAwesomeIcon
                  icon={faPlus}
                  size="lg"
                  color={
                    location.pathname === "/summarization-request"
                      ? "#141414"
                      : "white"
                  }
                />
              </div>
            </Link>
          </div>
          <MobileNavItem
            href="https://sharpana.supahub.com/"
            icon={<FontAwesomeIcon icon={faComment} size="lg" />}
            active={false}
          />
          <MobileNavItem
            to="/settings"
            icon={<FontAwesomeIcon icon={faUser} size="lg" />}
            active={location.pathname === "/settings"}
          />
        </nav>
      </>
    );
  }

  // Desktop top navigation
  return (
    <nav
      className="navbar navbar-expand-md navbar-dark bg-dark"
      css={css`
        display: flex;
        align-items: center;
        padding: ${_isWebExtension ? "8px 16px" : "16px 32px"};
        background-color: #141414;
        border-bottom: 1px solid #303030;
      `}
    >
      <div className="container-fluid">
        <Link
          to="/summarization-request"
          className="navbar-brand"
          onClick={handleClick}
        >
          <img
            src={sharpanaLogo}
            alt="Sharpana"
            css={css`
              height: 30px;
              margin-right: 0px;
            `}
          />
        </Link>
        <button
          className="navbar-toggler"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#navbarCollapse"
          aria-controls="navbarCollapse"
          aria-expanded="false"
          aria-label="Toggle navigation"
        >
          <span className="navbar-toggler-icon" />
        </button>
        <div className="collapse navbar-collapse" id="navbarCollapse">
          <ul className="navbar-nav me-auto mb-2 mb-md-0">
            <NavBarItem
              to="/feed"
              icon="🏠"
              name="Home"
              onClick={handleClick}
            />
            <NavBarItem
              to="/summarization-request"
              icon="➕"
              name="New Summary"
              onClick={handleClick}
            />
            <NavBarItem
              to="/history"
              icon="⌚︎"
              name="History"
              onClick={handleClick}
            />

            <NavBarItem
              href="https://sharpana.supahub.com/"
              icon="🪲"
              name="Bugs & Feedback"
              onClick={handleClick}
              openInNewTab={true}
            />

            {(window as any).DEVELOPMENT ? (
              <NavBarItem
                href="http://localhost:8080"
                icon="📨"
                name="MailCatcher"
                onClick={handleClick}
              />
            ) : null}
            {user ? authNav : anonNav}
          </ul>
        </div>
      </div>
    </nav>
  );
}
